import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePickerField } from '../components/ui/date-picker-field';
import { toast } from 'sonner';
import {
  Calendar, Users, Code2, Save, Filter,
  Building2, ChevronRight, ChevronDown, Layers, Flame, Download, Image as ImageIcon, Sparkles, Copy, Check,
  ArrowUp, ArrowDown, Minus, Undo2, Gauge, AlertTriangle, Search, X, Lock,
} from 'lucide-react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { getMaturityBand } from '../lib/utils';
import PageHelpButton from '../components/PageHelpButton';
import { toPng } from 'html-to-image';
import {
  DEV_TYPE, offsetDate, fmtDate, dayDiff,
  csvEscape, buildCapacityCsvRows, buildDevPlanCsvRows,
  WeekHeader, EmptyState, HighlightMatch,
  buildReleaseCols, releaseColsByIdx, RELEASE_COLORS,
  atRiskPhasesForProject,
} from './workspace-overview/shared';
import {
  DevelopmentView, PortfolioDevSplit, DevMixTrendChip,
} from './workspace-overview/DevelopmentView';
import { RosterView } from './workspace-overview/RosterView';
import { EntityRegionFilterChips, useEntityRegionOptions } from '../components/EntityRegionFilterChips';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Short labels for the Public-Cloud release legend chips.
const RELEASE_TYPE_LABEL_SHORT = { prod: 'Full', test: 'Test', maintenance: 'Maint.' };

// Two status groups (v260.54). Workflow = auto-derived by the approval
// system; Lifecycle = manual / side-effect-bearing states. The Resource
// view's filter chips render them as separate rows so leadership can scan
// "workflow stage" vs "deal lifecycle" independently.
const STATUS_GROUPS_FILTER = [
  {
    name: 'Workflow',
    options: [
      { id: 'draft', label: 'Draft' },
      { id: 'in_review', label: 'In Review' },
      { id: 'approved', label: 'Approved' },
      { id: 'changes_requested', label: 'Changes Req.' },
    ],
  },
  {
    name: 'Lifecycle',
    options: [
      { id: 'confirmed', label: 'Confirmed' },
      { id: 'won', label: 'Won' },
      { id: 'lost', label: 'Lost' },
      { id: 'archived', label: 'Archived' },
    ],
  },
];

const ALL_STATUSES = STATUS_GROUPS_FILTER.flatMap(g => g.options);

const SEGMENT_OPTIONS = [
  { id: 'implementation', label: 'Implementation' },
  { id: 'customer', label: 'Customer Project' },
  { id: 'rnd', label: 'Seertech R&D' },
];
const SEGMENT_TONE = {
  implementation: 'bg-sky-50 text-sky-700 border-sky-200',
  customer: 'bg-amber-50 text-amber-700 border-amber-200',
  rnd: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};
const SEGMENT_LABEL = { implementation: 'Implementation', customer: 'Customer Project', rnd: 'Seertech R&D' };

const STATUS_TONE = {

  draft: 'bg-slate-100 text-slate-600',
  in_review: 'bg-amber-100 text-amber-700',
  changes_requested: 'bg-rose-100 text-rose-700',
  confirmed: 'bg-indigo-100 text-indigo-700',
  approved: 'bg-emerald-100 text-emerald-700',
  won: 'bg-violet-100 text-violet-700',
  lost: 'bg-rose-100 text-rose-700',
};

const DISCIPLINE_COLOR = {
  'PM / Lead': '#0F2040',
  'Consulting': '#4ABFCF',
  'Development': '#8B5CF6',
  'Integration': '#10B981',
  'Data Migration': '#F59E0B',
  'QA': '#3B82F6',
  'Hypercare': '#EF4444',
  'Infrastructure': '#6B7280',
  'Other': '#94A3B8',
};

// v260.96 — Brand-aligned palette for the phase strip layered inside
// the Portfolio Project bar. Keys match the backend `phase_key` field
// emitted by `_classify_phase()` in `workspace_overview.py`. Hypercare
// renders as a striped lighter band so the post-go-live tail is
// visually distinct from the Execution body.
const PHASE_COLOR = {
  initiation: '#6366F1',   // indigo  — kickoff / mobilisation
  design:     '#4ABFCF',   // teal    — discovery / blueprint
  execution:  '#0F2040',   // brand   — build / config
  uat:        '#F59E0B',   // amber   — validation
  go_live:    '#10B981',   // emerald — cutover
  hypercare:  '#10B981',   // emerald (rendered hatched on FE)
  other:      '#94A3B8',   // slate   — fallback
};
const PHASE_ORDER = ['initiation', 'design', 'execution', 'uat', 'go_live', 'hypercare', 'other'];
const PHASE_LEGEND_LABEL = {
  initiation: 'Initiation',
  design: 'Design',
  execution: 'Execution',
  uat: 'UAT',
  go_live: 'Go-Live',
  hypercare: 'Hypercare',
};


// Color hint for the resource heatmap based on FTE count (1.0 = one full
// person-week). Single-row >=1.0 FTE shows the over-allocation rose tint
// because no individual role/discipline-on-a-single-project can exceed
// one full person without flagging capacity risk. Aggregated totals rows
// commonly need many FTE — we keep the same scale there since "this
// discipline needs ≥1 person this week" is exactly what we want to call
// attention to. Above 1.0 FTE, the rose tint deepens stepwise so leadership
// can spot the worst pressure points without reading every cell.
function fteTone(fte) {
  if (fte <= 0) return 'bg-slate-50';
  if (fte < 0.25) return 'bg-cyan-50';
  if (fte < 0.5) return 'bg-cyan-100';
  if (fte < 0.75) return 'bg-cyan-200';
  if (fte < 1) return 'bg-amber-200';
  if (fte < 1.5) return 'bg-rose-300';
  if (fte < 2.5) return 'bg-rose-400';
  if (fte < 4) return 'bg-rose-500';
  if (fte < 7) return 'bg-rose-600';
  return 'bg-rose-700';
}

// Text colour that pairs with the gradient — light rose backgrounds keep
// rose-900 (dark) for legibility; deeper rose backgrounds switch to white.
function fteTextTone(fte) {
  if (fte < 1) return 'text-slate-700';
  if (fte < 2.5) return 'text-rose-900 font-bold';
  return 'text-white font-bold';
}

// Format a 0-100+ percentage into FTE units (1 decimal, dropped to ''
// when essentially zero so the heatmap stays quiet).
function fmtFte(pct) {
  const fte = (pct || 0) / 100;
  if (fte < 0.05) return '';
  return fte.toFixed(1);
}

// CSV cell escape — wraps values containing commas / quotes / newlines and
// doubles up any embedded quotes per RFC 4180.
export default function WorkspaceOverviewPage() {
  const navigate = useNavigate();

  // v260.73 — URL state mirror so leadership can paste the current
  // view into Slack and a teammate lands on the exact same tab /
  // date window / status filter / granularity / compare-mode.
  // Read ONCE on mount via this frozen ref so subsequent re-renders
  // (which may already reflect user nudges) don't keep re-applying
  // the original URL. Lazy useState initialisers below consume this
  // ref to seed initial state before loadPrefs has a chance to fire,
  // and loadPrefs respects per-field URL overrides so a shared link
  // wins over the saved user preference for every field present.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSeedRef = React.useRef(null);
  if (urlSeedRef.current === null) {
    const ALLOWED_TABS = new Set(['project', 'resource', 'development', 'roster']);
    const ALLOWED_GRAN = new Set(['discipline', 'role']);
    const ALLOWED_MODES = new Set(['prior_window', 'yoy', 'prior_26w']);
    const ALLOWED_SORT = new Set(['name', 'start_date']);
    const ALLOWED_DIR = new Set(['asc', 'desc']);
    const ISO = /^\d{4}-\d{2}-\d{2}$/;
    const sp = searchParams; // captured once for the initial render
    const tabParam = sp.get('tab');
    const startParam = sp.get('start');
    const endParam = sp.get('end');
    const statusParam = sp.get('status');
    const granParam = sp.get('granularity');
    const modeParam = sp.get('mode');
    const sortParam = sp.get('sort');
    const dirParam = sp.get('dir');
    const qParam = sp.get('q');
    const segParam = sp.get('seg');
    urlSeedRef.current = {
      tab: tabParam && ALLOWED_TABS.has(tabParam) ? tabParam : null,
      start: startParam && ISO.test(startParam) ? startParam : null,
      end: endParam && ISO.test(endParam) ? endParam : null,
      status: statusParam !== null ? statusParam.split(',').filter(Boolean) : null,
      granularity: granParam && ALLOWED_GRAN.has(granParam) ? granParam : null,
      mode: modeParam && ALLOWED_MODES.has(modeParam) ? modeParam : null,
      sort: sortParam && ALLOWED_SORT.has(sortParam) ? sortParam : null,
      dir: dirParam && ALLOWED_DIR.has(dirParam) ? dirParam : null,
      q: qParam ? qParam.slice(0, 120) : null,
      segment: segParam !== null ? segParam.split(',').filter(Boolean) : null,
    };
  }
  const urlSeed = urlSeedRef.current;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // v261 — Default landing window = start of the current month → +12
  // months, so the portfolio opens anchored on "this month" with a
  // year-ahead horizon. Changeable via the date pickers; a saved user
  // preference (offset-based) wins once they've nudged the dates.
  const _now = new Date();
  const _monthStart = new Date(Date.UTC(_now.getUTCFullYear(), _now.getUTCMonth(), 1)).toISOString().slice(0, 10);
  const _monthPlus12 = new Date(Date.UTC(_now.getUTCFullYear(), _now.getUTCMonth() + 12, 0)).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(() => urlSeed.start || _monthStart);
  const [endDate, setEndDate] = useState(() => urlSeed.end || _monthPlus12);
  const [statusFilter, setStatusFilter] = useState(() => urlSeed.status || []);
  const [segmentFilter, setSegmentFilter] = useState(() => urlSeed.segment || []);
  // v380.1 — Entity / Region chip filters (same behaviour as Workspaces),
  // remembered per browser between visits.
  const [entityFilter, setEntityFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bp-overview-erf') || '{}').e || []; } catch { return []; }
  });
  const [regionFilter, setRegionFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bp-overview-erf') || '{}').r || []; } catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem('bp-overview-erf', JSON.stringify({ e: entityFilter, r: regionFilter }));
  }, [entityFilter, regionFilter]);
  // v382 — Effort & cost lock filter: '' (all) | 'locked' | 'unlocked'
  const [lockedFilter, setLockedFilter] = useState('');
  const { entities: erEntities, regions: erRegions } = useEntityRegionOptions();
  const [granularity, setGranularity] = useState(() => urlSeed.granularity || 'discipline'); // 'role' | 'discipline'
  // v261 — Portfolio display order, applied to every tab that lists
  // projects (Project / Resource breakdown / Development) and to the
  // Roster's people rows (by person name).
  const [sortField, setSortField] = useState(() => urlSeed.sort || 'name'); // 'name' | 'start_date'
  const [sortDir, setSortDir] = useState(() => urlSeed.dir || 'asc'); // 'asc' | 'desc'
  // v261 — Quick search: type a project name to instantly filter every
  // tab. Transient (not saved to prefs) but shareable via the URL `q`.
  const [searchQuery, setSearchQuery] = useState(() => urlSeed.q || '');
  // Auto-save status badge — 'idle' | 'saving' | 'saved'. The badge briefly
  // appears as "Saved" after each successful auto-PUT so users get feedback
  // without a toast spam every time they nudge a filter.
  const [prefsSaveState, setPrefsSaveState] = useState('idle');
  // Undo support — captures a snapshot of the prefs object *before* the
  // most recent user nudge. `undoArmed` flips true when a snapshot exists
  // and the current state differs from it, so the chip only renders when
  // there's an actual rollback target.
  const [prevPrefs, setPrevPrefs] = useState(null);
  const [undoArmed, setUndoArmed] = useState(false);
  const prefsHydratedRef = React.useRef(false);
  const prefsSaveTimerRef = React.useRef(null);
  const prevPrefsCaptureRef = React.useRef(null); // throttles "snapshot every keystroke"
  const skipNextSnapshotRef = React.useRef(false); // set during undo to prevent re-arming
  const [tab, setTab] = useState(() => urlSeed.tab || 'project');
  const [riskFilter, setRiskFilter] = useState(false); // v260.138 — show only release-at-risk projects
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [briefingText, setBriefingText] = useState('');
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingCopied, setBriefingCopied] = useState(false);
  const [includeBriefingInPng, setIncludeBriefingInPng] = useState(false);
  const [priorDevSplit, setPriorDevSplit] = useState(null); // { new_dev, integration, migration } from the prior same-length window
  const [compareMode, setCompareMode] = useState(() => urlSeed.mode || 'prior_window'); // 'prior_window' | 'yoy' | 'prior_26w'

  const loadPrefs = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/workspace-overview/preferences`);
      const p = r.data || {};
      // v260.73 — URL params win over saved prefs for every field
      // that's present in the URL. This makes shareable links
      // deterministic: a recipient sees exactly what the sharer saw,
      // regardless of their own saved defaults.
      let nextStart = startDate, nextEnd = endDate;
      if (urlSeed.start) {
        nextStart = urlSeed.start;
      } else if (typeof p.default_start_offset_weeks === 'number') {
        nextStart = offsetDate(p.default_start_offset_weeks);
        setStartDate(nextStart);
      }
      if (urlSeed.end) {
        nextEnd = urlSeed.end;
      } else if (typeof p.default_end_offset_weeks === 'number') {
        nextEnd = offsetDate(p.default_end_offset_weeks);
        setEndDate(nextEnd);
      }
      const nextStatus = urlSeed.status
        || (Array.isArray(p.default_status_filter) ? p.default_status_filter : []);
      const nextGran = urlSeed.granularity || p.default_granularity || 'discipline';
      const nextMode = urlSeed.mode || p.default_dev_mix_compare_mode || 'prior_window';
      const nextSort = urlSeed.sort || p.default_sort_field || 'name';
      const nextDir = urlSeed.dir || p.default_sort_direction || 'asc';
      if (!urlSeed.status) setStatusFilter(nextStatus);
      if (!urlSeed.granularity) setGranularity(nextGran);
      if (!urlSeed.mode) setCompareMode(nextMode);
      if (!urlSeed.sort) setSortField(nextSort);
      if (!urlSeed.dir) setSortDir(nextDir);
      // Seed the committed-prefs ref so the auto-save effect's first run
      // (post-hydration) doesn't see a phantom diff and capture an undo
      // snapshot from the initial defaults.
      prevPrefsCaptureRef.current = {
        startDate: nextStart, endDate: nextEnd,
        statusFilter: nextStatus, granularity: nextGran, compareMode: nextMode,
        sortField: nextSort, sortDir: nextDir,
      };
    } catch (e) { logger.warn('prefs load failed', e); }
    finally {
      // Mark hydration complete on the NEXT tick so the auto-save effect's
      // initial run (firing from setX calls above) is skipped — we only
      // want to PUT when the user actually nudges something.
      setTimeout(() => { prefsHydratedRef.current = true; }, 0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/workspace-overview`, {
        start_date: startDate,
        end_date: endDate,
        status_filter: statusFilter.length ? statusFilter : null,
        segment_filter: segmentFilter.length ? segmentFilter : null,
        entity_filter: entityFilter.length ? entityFilter : null,
        region_filter: regionFilter.length ? regionFilter : null,
        locked_filter: lockedFilter || null,
      });
      setData(r.data);
    } catch (e) {
      logger.error('overview fetch failed', e);
      toast.error('Could not load portfolio overview');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, statusFilter, segmentFilter, entityFilter, regionFilter, lockedFilter]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  // Fetch the prior same-length window so the Development tab can render
  // a trend arrow on the portfolio dev-mix headline. The prior window
  // depends on `compareMode`:
  //   - 'prior_window' → same span, immediately preceding [start − span, start)
  //   - 'yoy'          → same window, shifted back exactly 1 year (seasonality)
  //   - 'prior_26w'    → fixed 26 weeks immediately preceding `start` regardless
  //                      of the current span (useful when current window is
  //                      narrow but you still want a sensible reference)
  const fetchPriorDevSplit = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      const a = new Date(startDate + 'T00:00:00Z');
      const b = new Date(endDate + 'T00:00:00Z');
      const spanMs = b.getTime() - a.getTime();
      if (spanMs <= 0) return;
      let priorStart, priorEnd;
      if (compareMode === 'yoy') {
        priorEnd = new Date(b.getTime() - 365 * 24 * 3600 * 1000);
        priorStart = new Date(a.getTime() - 365 * 24 * 3600 * 1000);
      } else if (compareMode === 'prior_26w') {
        priorEnd = new Date(a.getTime() - 24 * 3600 * 1000);
        priorStart = new Date(priorEnd.getTime() - 26 * 7 * 24 * 3600 * 1000);
      } else {
        // prior_window
        priorEnd = new Date(a.getTime() - 24 * 3600 * 1000);
        priorStart = new Date(priorEnd.getTime() - spanMs);
      }
      const iso = (d) => d.toISOString().slice(0, 10);
      const r = await axios.post(`${API_URL}/api/workspace-overview`, {
        start_date: iso(priorStart),
        end_date: iso(priorEnd),
        status_filter: statusFilter.length ? statusFilter : null,
        segment_filter: segmentFilter.length ? segmentFilter : null,
        entity_filter: entityFilter.length ? entityFilter : null,
        region_filter: regionFilter.length ? regionFilter : null,
      });
      const totals = (r.data?.projects || []).reduce((acc, p) => {
        for (const ph of (p.dev_phases || [])) {
          const t = ph.type || 'other';
          acc[t] = (acc[t] || 0) + (ph.hours || 0);
        }
        return acc;
      }, {});
      setPriorDevSplit(totals);
    } catch (e) {
      logger.warn('prior dev-split fetch failed', e);
      setPriorDevSplit(null);
    }
  }, [startDate, endDate, statusFilter, segmentFilter, entityFilter, regionFilter, compareMode]);

  // Invalidate + refetch the prior split whenever the user lands on the
  // Development tab OR the date / status filters change while already
  // there. Outside of the Dev tab we leave the cache as-is.
  useEffect(() => {
    if (tab === 'development') fetchPriorDevSplit();
  }, [tab, fetchPriorDevSplit]);

  // Debounced auto-save: any time the user nudges a pref (dates, status,
  // granularity, compare mode), schedule a PUT 700ms later. Skipped on
  // initial hydration so we don't echo the just-loaded defaults back.
  useEffect(() => {
    if (!prefsHydratedRef.current) return;
    // Snapshot the *previously committed* state before this change goes
    // through — gives the undo chip something to roll back to. We capture
    // only on the FIRST nudge of a save-batch (subsequent keystrokes within
    // the same debounce window keep the original snapshot). Skipped when
    // the change came from an undo click (otherwise the undo itself would
    // re-arm a new rollback target).
    if (skipNextSnapshotRef.current) {
      skipNextSnapshotRef.current = false;
    } else {
      const committed = prevPrefsCaptureRef.current;
      if (committed && !undoArmed) {
        const changed = committed.startDate !== startDate
          || committed.endDate !== endDate
          || JSON.stringify(committed.statusFilter) !== JSON.stringify(statusFilter)
          || committed.granularity !== granularity
          || committed.compareMode !== compareMode
          || committed.sortField !== sortField
          || committed.sortDir !== sortDir;
        if (changed) {
          setPrevPrefs(committed);
          setUndoArmed(true);
        }
      }
    }
    if (prefsSaveTimerRef.current) clearTimeout(prefsSaveTimerRef.current);
    prefsSaveTimerRef.current = setTimeout(async () => {
      setPrefsSaveState('saving');
      try {
        const today = new Date();
        // Guard: cleared dates send `null` for the offset so the backend
        // applies its own default (today / today+26w) on the next fetch
        // rather than failing Pydantic validation on NaN.
        const startOffset = startDate
          ? Math.round((new Date(startDate + 'T00:00:00Z') - today) / (7 * 86400000))
          : null;
        const endOffset = endDate
          ? Math.round((new Date(endDate + 'T00:00:00Z') - today) / (7 * 86400000))
          : null;
        // v261 — when the window sits exactly on the month-anchored
        // default, un-pin the saved offsets so the default keeps rolling
        // forward with the calendar instead of freezing to this week.
        const atAnchor = startDate === _monthStart && endDate === _monthPlus12;
        await axios.put(`${API_URL}/api/workspace-overview/preferences`, {
          default_start_offset_weeks: atAnchor ? null : startOffset,
          default_end_offset_weeks: atAnchor ? null : endOffset,
          clear_window: atAnchor || undefined,
          default_status_filter: statusFilter,
          default_granularity: granularity,
          default_dev_mix_compare_mode: compareMode,
          default_sort_field: sortField,
          default_sort_direction: sortDir,
        });
        // Promote the just-saved state to "committed" so the next nudge can
        // snapshot it for a future undo.
        prevPrefsCaptureRef.current = { startDate, endDate, statusFilter, granularity, compareMode, sortField, sortDir };
        setPrefsSaveState('saved');
        setTimeout(() => setPrefsSaveState('idle'), 1800);
      } catch (e) {
        logger.warn('auto-save prefs failed', e);
        setPrefsSaveState('idle');
      }
    }, 700);
    return () => {
      if (prefsSaveTimerRef.current) clearTimeout(prefsSaveTimerRef.current);
    };
  }, [startDate, endDate, statusFilter, granularity, compareMode, sortField, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  // v260.73 — URL state mirror. After prefs hydration, mirror every
  // user-facing filter into the query string so the current view is
  // shareable via simple copy/paste. Uses replace=true so back-button
  // history doesn't fill up with every filter nudge.
  useEffect(() => {
    if (!prefsHydratedRef.current) return;
    const next = new URLSearchParams();
    if (tab && tab !== 'project') next.set('tab', tab);
    if (startDate) next.set('start', startDate);
    if (endDate) next.set('end', endDate);
    if (statusFilter.length > 0) next.set('status', statusFilter.join(','));
    if (segmentFilter.length > 0) next.set('seg', segmentFilter.join(','));
    if (granularity && granularity !== 'discipline') next.set('granularity', granularity);
    if (compareMode && compareMode !== 'prior_window') next.set('mode', compareMode);
    if (sortField && sortField !== 'name') next.set('sort', sortField);
    if (sortDir && sortDir !== 'asc') next.set('dir', sortDir);
    if (searchQuery.trim()) next.set('q', searchQuery.trim());
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, startDate, endDate, statusFilter, segmentFilter, granularity, compareMode, sortField, sortDir, searchQuery]);


  const undoLastChange = useCallback(() => {
    if (!prevPrefs) return;
    // Restoring the previous snapshot will trigger another auto-save which
    // commits the rollback. Skip the snapshot capture for that save so the
    // undo chip doesn't immediately re-arm itself as if the rollback were
    // a new user nudge.
    skipNextSnapshotRef.current = true;
    setStartDate(prevPrefs.startDate);
    setEndDate(prevPrefs.endDate);
    setStatusFilter(prevPrefs.statusFilter);
    setGranularity(prevPrefs.granularity);
    setCompareMode(prevPrefs.compareMode);
    if (prevPrefs.sortField) setSortField(prevPrefs.sortField);
    if (prevPrefs.sortDir) setSortDir(prevPrefs.sortDir);
    setUndoArmed(false);
    setPrevPrefs(null);
    toast.success('Reverted to previous view');
  }, [prevPrefs]);

  const toggleStatus = (id) => {
    setStatusFilter((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSegment = (id) => {
    setSegmentFilter((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // v261 — Apply quick search + the chosen display order once, shared by
  // every tab. Name → locale A-Z; Project start → ISO date compare with
  // undated projects always last (regardless of direction).
  const viewData = useMemo(() => {
    if (!data) return data;
    const dir = sortDir === 'desc' ? -1 : 1;
    const q = searchQuery.trim().toLowerCase();
    const byName = (a, b) => dir * (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    let projects = data.projects || [];
    if (q) projects = projects.filter(p => (p.name || '').toLowerCase().includes(q));
    projects = [...projects].sort((a, b) => {
      if (sortField === 'start_date') {
        const sa = a.start_date || '';
        const sb = b.start_date || '';
        if (!sa || !sb) return (!sa && !sb) ? byName(a, b) : (!sa ? 1 : -1);
        if (sa !== sb) return dir * sa.localeCompare(sb);
      }
      return byName(a, b);
    });
    return { ...data, projects };
  }, [data, sortField, sortDir, searchQuery]);

  const exportCSV = useCallback(() => {  if (!viewData) return;
    try {
      const rows = buildCapacityCsvRows(viewData, granularity);
      const csv = rows.map(r => r.map(csvEscape).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const range = `${data?.date_range?.start || 'start'}-to-${data?.date_range?.end || 'end'}`;
      a.href = url;
      a.download = `seertech-portfolio-capacity-${range}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Portfolio capacity exported');
    } catch (e) {
      logger.warn('csv export failed', e);
      toast.error('Could not export CSV');
    }
  }, [viewData, granularity]);

  // v260.73 — Copy a fully-qualified shareable URL that captures the
  // current tab + filters. Uses window.location.href which the URL-
  // sync effect keeps up to date; no need to re-compute the query
  // string here.
  const [linkCopied, setLinkCopied] = useState(false);
  const copyShareLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      toast.success('Shareable link copied to clipboard');
      setTimeout(() => setLinkCopied(false), 1800);
    } catch (e) {
      logger.warn('copy share link failed', e);
      toast.error('Could not copy link');
    }
  }, []);

  const fetchBriefing = useCallback(async () => {
    const r = await axios.post(`${API_URL}/api/workspace-overview/briefing`, {
      status_filter: statusFilter,
      segment_filter: segmentFilter.length ? segmentFilter : null,
      start_date: startDate,
      end_date: endDate,
      granularity,
      fte_hours_per_week: 40,
    });
    return r.data?.briefing || '';
  }, [statusFilter, segmentFilter, startDate, endDate, granularity]);

  const copyPng = useCallback(async () => {
    if (!data) return;
    const region = document.querySelector('[data-testid="overview-export-region"]');
    if (!region) {
      toast.error('Switch to the Resource tab first');
      return;
    }
    const heatmapScroller = region.querySelector('[data-testid="overview-resource-view"]');
    const prevOverflow = heatmapScroller?.style.overflow;
    const prevWidth = heatmapScroller?.style.width;
    const skipEls = Array.from(region.querySelectorAll('[data-export-skip="true"]'));
    const prevDisplays = skipEls.map(el => el.style.display);
    skipEls.forEach(el => { el.style.display = 'none'; });

    const t = toast.loading(includeBriefingInPng ? 'Drafting briefing + capturing heatmap…' : 'Capturing heatmap…');
    let briefingNode = null;

    try {
      // Fetch + inject briefing footer when the toggle is on. Re-fetch on
      // every PNG capture so the narrative is always anchored to the same
      // filter state as the heatmap image.
      if (includeBriefingInPng) {
        let txt = '';
        try { txt = await fetchBriefing(); } catch (briefErr) { logger.warn('briefing fetch failed mid-capture', briefErr); }
        if (txt) {
          briefingNode = document.createElement('div');
          briefingNode.setAttribute('data-export-briefing', 'true');
          briefingNode.style.cssText = 'margin-top:12px;padding:14px 16px;border-radius:8px;background:#F5F3FF;border:1px solid #DDD6FE;display:flex;gap:10px;align-items:flex-start;';
          briefingNode.innerHTML = `
            <div style="color:#7C3AED;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;white-space:nowrap;padding-top:2px;">AI Briefing</div>
            <div style="color:#0F2040;font-size:13px;line-height:1.55;">${txt.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
          `;
          region.appendChild(briefingNode);
        }
      }

      if (heatmapScroller) {
        heatmapScroller.style.overflow = 'visible';
        heatmapScroller.style.width = `${heatmapScroller.scrollWidth}px`;
      }
      const pngDataUrl = await toPng(region, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#FFFFFF',
        width: region.scrollWidth,
        height: region.scrollHeight,
        style: { background: '#FFFFFF' },
      });
      const res = await fetch(pngDataUrl);
      const blob = await res.blob();
      let clipboardOk = false;
      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          clipboardOk = true;
        } catch (clipErr) {
          logger.warn('clipboard write blocked, falling back to download', clipErr);
        }
      }
      toast.dismiss(t);
      if (clipboardOk) {
        toast.success(includeBriefingInPng ? 'Heatmap + briefing copied' : 'Heatmap copied — paste into your slide');
      } else {
        const range = `${data?.date_range?.start || 'start'}-to-${data?.date_range?.end || 'end'}`;
        const a = document.createElement('a');
        a.href = pngDataUrl;
        a.download = `seertech-portfolio-capacity-${range}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success('Heatmap downloaded as PNG');
      }
    } catch (e) {
      logger.warn('png export failed', e);
      toast.dismiss(t);
      toast.error('Could not capture heatmap');
    } finally {
      if (heatmapScroller) {
        heatmapScroller.style.overflow = prevOverflow || '';
        heatmapScroller.style.width = prevWidth || '';
      }
      skipEls.forEach((el, i) => { el.style.display = prevDisplays[i] || ''; });
      if (briefingNode && briefingNode.parentNode) {
        briefingNode.parentNode.removeChild(briefingNode);
      }
    }
  }, [data, includeBriefingInPng, fetchBriefing]);

  const generateBriefing = useCallback(async () => {
    if (!data) return;
    setBriefingOpen(true);
    setBriefingLoading(true);
    setBriefingCopied(false);
    setBriefingText('');
    try {
      const text = await fetchBriefing();
      setBriefingText(text);
    } catch (e) {
      logger.warn('briefing failed', e);
      toast.error(e?.response?.data?.detail || 'Could not generate briefing');
      setBriefingOpen(false);
    } finally {
      setBriefingLoading(false);
    }
  }, [data, fetchBriefing]);

  const copyBriefing = useCallback(async () => {
    if (!briefingText) return;
    try {
      await navigator.clipboard.writeText(briefingText);
      setBriefingCopied(true);
      setTimeout(() => setBriefingCopied(false), 1400);
    } catch (_) {
      toast.error('Copy failed — select and copy manually');
    }
  }, [briefingText]);

  const totalWeeks = data?.date_range?.weeks || 0;
  const weekLabels = data?.date_range?.week_labels || [];

  // Today indicator week index
  const todayIdx = useMemo(() => {
    if (!data?.today || !weekLabels.length) return -1;
    const offsetDays = dayDiff(weekLabels[0], data.today);
    return Math.floor(offsetDays / 7);
  }, [data, weekLabels]);

  // v260.138 — Portfolio-wide release-risk summary: how many projects
  // have deliverables that slip past their anchored release date.
  const riskSummary = useMemo(() => {
    let projCount = 0, delivCount = 0, anchored = 0;
    for (const p of (data?.projects || [])) {
      let risky = 0;
      for (const ph of (p.dev_phases || [])) {
        if (ph.anchor_release_label) anchored += 1;
        if (ph.release_at_risk) risky += 1;
      }
      if (risky > 0) { projCount += 1; delivCount += risky; }
    }
    return { projCount, delivCount, anchored };
  }, [data]);

  // Auto-clear the risk filter if a data/date change removes all risk.
  useEffect(() => {
    if (riskFilter && riskSummary.delivCount === 0) setRiskFilter(false);
  }, [riskFilter, riskSummary.delivCount]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="workspace-overview-page">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0F2040] flex items-center gap-2">
              <Layers className="w-6 h-6 text-[#4ABFCF]" /> Portfolio Overview
              <PageHelpButton section="4-the-portfolio-view" label="Portfolio — open the user guide" />
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Macro view across every project in the workspace — start/end alignment, resource peaks, development hot-spots.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={generateBriefing} disabled={!data} data-testid="overview-ai-briefing"
              className="border-violet-300 text-violet-700 hover:bg-violet-50">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Briefing
            </Button>
            {/* Consolidated export & share menu (v366 nav simplification) */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="overview-export-menu-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Export &amp; Share
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-2">
                <div className="flex flex-col gap-1.5 [&>button]:w-full [&>button]:justify-start" data-testid="overview-export-menu">
                  <Button variant="ghost" size="sm" onClick={copyPng} disabled={!data || tab !== 'resource'} data-testid="overview-copy-png"
                    title={tab !== 'resource' ? 'Switch to the Resource tab to copy the heatmap' : 'Copy heatmap as PNG'}>
                    <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Copy as PNG
                  </Button>
                  <div className="flex items-center gap-2 px-3 py-1">
                    <Switch
                      id="include-briefing-toggle"
                      checked={includeBriefingInPng}
                      onCheckedChange={setIncludeBriefingInPng}
                      data-testid="overview-include-briefing-toggle"
                      className="data-[state=checked]:bg-violet-600 scale-75"
                    />
                    <Label htmlFor="include-briefing-toggle" className="text-[11px] text-slate-600 cursor-pointer select-none">
                      Include AI briefing in PNG
                    </Label>
                  </div>
                  <Button variant="ghost" size="sm" onClick={exportCSV} disabled={!data} data-testid="overview-export-csv">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
                  </Button>
                  <Button variant="ghost" size="sm" onClick={copyShareLink} data-testid="overview-copy-link"
                    title="Copy a link that opens this exact view (filters + tab) for anyone you share it with">
                    {linkCopied ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                    {linkCopied ? 'Link copied' : 'Copy link'}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            {undoArmed && (
              <button
                type="button"
                onClick={undoLastChange}
                data-testid="overview-undo-last-change"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-200 bg-amber-50 text-amber-700 text-[11px] font-medium hover:bg-amber-100 transition"
                title="Revert to your previous view (last filter / date / granularity / compare mode)"
              >
                <Undo2 className="w-3 h-3" />
                <span>Undo last change</span>
              </button>
            )}
            <PrefsSaveBadge state={prefsSaveState} />
          </div>
        </header>

        <Card className="mb-4">
          <CardContent className="p-3 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Start</Label>
              <DatePickerField
                value={startDate}
                onChange={setStartDate}
                testId="overview-start-date"
                placeholder="Pick start"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">End</Label>
              <DatePickerField
                value={endDate}
                onChange={setEndDate}
                testId="overview-end-date"
                placeholder="Pick end"
                minDate={startDate}
                anchorDate={startDate}
                quickRanges
              />
            </div>
            <button
              type="button"
              onClick={() => { setStartDate(_monthStart); setEndDate(_monthPlus12); }}
              disabled={startDate === _monthStart && endDate === _monthPlus12}
              data-testid="overview-window-reset"
              title="Anchor the window to the start of the current month + 12 months (the default view)"
              className="text-[10px] px-2 py-1 mb-1 rounded-md border border-slate-200 bg-white text-slate-500 hover:border-[#4ABFCF] hover:text-[#0F2040] transition-colors disabled:opacity-40 disabled:cursor-default"
            >
              This month +12m
            </button>
            <div className="min-w-[190px]">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                <Search className="w-3 h-3" /> Search
              </Label>
              <div className="relative mt-0.5">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Filter by project name…"
                  className="h-8 text-xs pr-7"
                  data-testid="overview-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    data-testid="overview-search-clear"
                    title="Clear search"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                <Filter className="w-3 h-3" /> Status
              </Label>
              {/* Compact filters popover (v367) — mirrors the Workspaces Filters dropdown */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 mt-0.5 text-xs text-slate-600" data-testid="overview-filters-btn">
                    <Filter className="w-3 h-3 mr-1" /> Filters
                    {(statusFilter.length + segmentFilter.length + entityFilter.length + regionFilter.length + (lockedFilter ? 1 : 0)) > 0 && (
                      <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#4ABFCF] text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="overview-filters-count">{statusFilter.length + segmentFilter.length + entityFilter.length + regionFilter.length + (lockedFilter ? 1 : 0)}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[420px] p-3" data-testid="overview-filters-panel">
                  <div className="space-y-2">
                    <div>
                      <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Type</div>
                      <div className="flex flex-wrap items-center gap-1">
                        {SEGMENT_OPTIONS.map((s) => (
                          <button key={s.id} type="button" onClick={() => toggleSegment(s.id)}
                            data-testid={`overview-segment-${s.id}`}
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${
                              segmentFilter.includes(s.id)
                                ? 'bg-[#0F2040] text-white border-[#0F2040]'
                                : 'border-slate-200 text-slate-500 hover:border-slate-300'
                            }`}>{s.label}</button>
                        ))}
                      </div>
                    </div>
                    <EntityRegionFilterChips
                      entities={erEntities} regions={erRegions}
                      entityFilter={entityFilter} regionFilter={regionFilter}
                      setEntityFilter={setEntityFilter} setRegionFilter={setRegionFilter}
                      idPrefix="overview" />
                    <div>
                      <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Effort &amp; cost lock</div>
                      <div className="flex flex-wrap items-center gap-1">
                        {[['', 'All'], ['locked', 'Locked'], ['unlocked', 'Unlocked']].map(([id, label]) => (
                          <button key={id || 'all'} type="button" onClick={() => setLockedFilter(id)}
                            data-testid={`overview-locked-${id || 'all'}`}
                            className={`text-[10px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${
                              lockedFilter === id
                                ? 'bg-[#0F2040] text-white border-[#0F2040]'
                                : 'border-slate-200 text-slate-500 hover:border-slate-300'
                            }`}>{id === 'locked' && <Lock className="w-2.5 h-2.5" />}{label}</button>
                        ))}
                      </div>
                    </div>
                    {STATUS_GROUPS_FILTER.map((g) => (
                      <div key={g.name}>
                        <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">{g.name}</div>
                        <div className="flex flex-wrap items-center gap-1">
                          {g.options.map((s) => (
                            <button key={s.id} type="button" onClick={() => toggleStatus(s.id)}
                              data-testid={`overview-status-${s.id}`}
                              className={`text-[10px] px-2 py-0.5 rounded-full border ${
                                statusFilter.includes(s.id)
                                  ? 'bg-[#0F2040] text-white border-[#0F2040]'
                                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
                              }`}>{s.label}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {(statusFilter.length > 0 || segmentFilter.length > 0 || entityFilter.length > 0 || regionFilter.length > 0 || lockedFilter) && (
                      <button onClick={() => { setStatusFilter([]); setSegmentFilter([]); setEntityFilter([]); setRegionFilter([]); setLockedFilter(''); }} className="text-[10px] text-slate-400 underline px-1" data-testid="overview-status-clear">clear all</button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Granularity</Label>
              <div className="flex gap-0 mt-0.5" data-testid="overview-granularity">
                {['discipline', 'role'].map((g) => (
                  <button key={g} type="button" onClick={() => setGranularity(g)}
                    data-testid={`overview-granularity-${g}`}
                    className={`text-[10px] px-2.5 py-1 border first:rounded-l last:rounded-r ${
                      granularity === g ? 'bg-[#4ABFCF] text-white border-[#4ABFCF]' : 'bg-white text-slate-500 border-slate-200'
                    }`}>{g === 'discipline' ? 'Discipline' : 'Role'}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Order by</Label>
              <div className="flex items-center gap-1 mt-0.5">
                <div className="flex gap-0" data-testid="overview-sort-field">
                  {[['name', 'Name'], ['start_date', 'Project start']].map(([id, label]) => (
                    <button key={id} type="button" onClick={() => setSortField(id)}
                      data-testid={`overview-sort-${id}`}
                      className={`text-[10px] px-2.5 py-1 border first:rounded-l last:rounded-r ${
                        sortField === id ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-500 border-slate-200'
                      }`}>{label}</button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
                  data-testid="overview-sort-direction"
                  title={sortDir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
                  className="text-[10px] px-1.5 py-1 border rounded bg-white text-slate-500 border-slate-200 hover:border-slate-300 inline-flex items-center gap-0.5"
                >
                  {sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  {sortDir === 'asc' ? 'Asc' : 'Desc'}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mb-2 text-xs text-slate-500" data-testid="overview-meta">
          {loading
            ? 'Loading…'
            : `${data?.project_count || 0} projects${searchQuery.trim() ? ` · ${viewData?.projects?.length || 0} match "${searchQuery.trim()}"` : ''} · ${totalWeeks} weeks · ${fmtDate(data?.date_range?.start)} → ${fmtDate(data?.date_range?.end)}`}
        </div>

        {/* v260.138 — Portfolio release-risk summary tile. Click to filter
            the Project / Development timelines to just at-risk projects. */}
        {riskSummary.anchored > 0 && (
          riskSummary.delivCount > 0 ? (
            <button
              type="button"
              onClick={() => {
                const next = !riskFilter;
                setRiskFilter(next);
                if (next && tab !== 'project' && tab !== 'development') setTab('development');
              }}
              className={`mb-3 w-full sm:w-auto flex items-center gap-3 rounded-lg border px-3.5 py-2 text-left transition-colors ${
                riskFilter
                  ? 'bg-rose-600 border-rose-600 text-white shadow-sm'
                  : 'bg-rose-50 border-rose-200 text-rose-800 hover:border-rose-400'
              }`}
              data-testid="overview-release-risk-tile"
              title="Deliverables that finish after their anchored release date"
            >
              <AlertTriangle className={`w-4 h-4 shrink-0 ${riskFilter ? 'text-white' : 'text-rose-500'}`} />
              <span className="text-sm font-semibold">
                {riskSummary.projCount} project{riskSummary.projCount === 1 ? '' : 's'} · {riskSummary.delivCount} deliverable{riskSummary.delivCount === 1 ? '' : 's'} at risk
              </span>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${riskFilter ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-700'}`}>
                {riskFilter ? 'Showing at-risk only · clear' : 'Click to filter'}
              </span>
            </button>
          ) : (
            <div
              className="mb-3 w-full sm:w-auto inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-emerald-700"
              data-testid="overview-release-risk-tile-ontrack"
            >
              <Check className="w-4 h-4 shrink-0 text-emerald-500" />
              <span className="text-sm font-medium">All anchored deliverables on track</span>
            </div>
          )
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="overview-tabs">
            <TabsTrigger value="project" data-testid="overview-tab-project"><Calendar className="w-3.5 h-3.5 mr-1.5" />Project</TabsTrigger>
            <TabsTrigger value="resource" data-testid="overview-tab-resource"><Users className="w-3.5 h-3.5 mr-1.5" />Resource</TabsTrigger>
            <TabsTrigger value="development" data-testid="overview-tab-development"><Code2 className="w-3.5 h-3.5 mr-1.5" />Development</TabsTrigger>
            <TabsTrigger value="roster" data-testid="overview-tab-roster"><Users className="w-3.5 h-3.5 mr-1.5" />Roster</TabsTrigger>
          </TabsList>

          <TabsContent value="project">
            <ProjectView data={viewData} weekLabels={weekLabels} todayIdx={todayIdx} onOpen={(id) => navigate(`/planner?id=${id}`)} navigate={navigate} riskOnly={riskFilter} searchQuery={searchQuery} />
          </TabsContent>
          <TabsContent value="resource">
            <ResourceView data={viewData} weekLabels={weekLabels} todayIdx={todayIdx} granularity={granularity} onOpen={(id) => navigate(`/planner?id=${id}`)} searchQuery={searchQuery} />
          </TabsContent>
          <TabsContent value="development">
            <DevelopmentView data={viewData} weekLabels={weekLabels} todayIdx={todayIdx} priorDevSplit={priorDevSplit} compareMode={compareMode} onCompareModeChange={setCompareMode} onOpen={(id) => navigate(`/planner?id=${id}`)} riskOnly={riskFilter} searchQuery={searchQuery} />
          </TabsContent>
          <TabsContent value="roster">
            <RosterView
              onOpenEstimate={(id) => navigate(`/planner?id=${id}`)}
              portfolioData={viewData}
              weekLabels={weekLabels}
              todayIdx={todayIdx}
              sortField={sortField}
              sortDir={sortDir}
              searchQuery={searchQuery}
            />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={briefingOpen} onOpenChange={setBriefingOpen}>
        <DialogContent className="max-w-xl" data-testid="overview-briefing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
              <Sparkles className="w-4 h-4 text-violet-500" /> AI Capacity Briefing
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500 -mt-2 mb-2">
            Two-sentence talk-track drafted from the current portfolio totals — paste-ready for your next leadership deck.
          </div>
          {briefingLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-4" data-testid="overview-briefing-loading">
              <Sparkles className="w-4 h-4 animate-pulse text-violet-500" />
              <span>Drafting briefing with Claude…</span>
            </div>
          ) : (
            <div className="rounded-md border border-violet-100 bg-violet-50/40 px-3 py-2.5 text-sm text-[#0F2040] leading-relaxed whitespace-pre-wrap"
              data-testid="overview-briefing-text">
              {briefingText || '(no content)'}
            </div>
          )}
          <DialogFooter className="mt-2 sm:justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={generateBriefing} disabled={briefingLoading} data-testid="overview-briefing-regenerate">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Regenerate
            </Button>
            <Button size="sm" onClick={copyBriefing} disabled={!briefingText || briefingLoading} data-testid="overview-briefing-copy"
              className="bg-violet-600 hover:bg-violet-700 text-white">
              {briefingCopied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
              {briefingCopied ? 'Copied' : 'Copy to clipboard'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Build the row matrix for the Development-plan CSV export.
// Emitted as one row per classified band (New Development / Integration /
// Data Migration) across every project in scope — pre-filtered by the
// current date window + status filters since `data.projects` already
// reflects those choices upstream. Designed for finance / capacity-
// planning hand-offs where the recipient lives in Excel.
//
//   # Seertech Blueprint Studio — Development Plan
//   # Date range / Project count / Total hours by type
//
//   Project,Type,Item,Group,Start,End,Hours
//   TEST Org 869474,Integration,SSO,Integrations,2026-05-04,2026-07-13,38.4
//   …
function PrefsSaveBadge({ state }) {
  let dotClass = 'bg-slate-300';
  let text = 'Auto-saved';
  let tone = 'text-slate-500';
  if (state === 'saving') {
    dotClass = 'bg-amber-400 animate-pulse';
    text = 'Saving…';
    tone = 'text-amber-700';
  } else if (state === 'saved') {
    dotClass = 'bg-emerald-500';
    text = 'Saved';
    tone = 'text-emerald-700';
  }
  return (
    <div
      data-testid="overview-prefs-save-badge"
      data-state={state}
      className={`inline-flex items-center gap-1.5 text-[11px] ${tone} pl-2`}
      title="View preferences (dates, filters, granularity, compare mode) auto-save as you change them"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
      <Save className="w-3 h-3" />
      <span>{text}</span>
    </div>
  );
}

function ProjectView({ data, weekLabels, todayIdx, onOpen, navigate, riskOnly = false, searchQuery = '' }) {
  // ALL HOOKS must be declared before any conditional returns to keep
  // React's hook order stable across re-renders (esp. on data load).
  // v260.98 — Hot-rows filter: surfaces projects that need attention
  // this week. Criteria toggled by the chip in the view header.
  // v260.100 — `hotFilter` is now a tri-state instead of a bool so
  // the breakdown chips can act as one-click sub-filters:
  //   null            → all projects (filter off)
  //   'all'           → any-reason hot (legacy "Hot rows only")
  //   'uat' | 'hypercare' | 'go_live_soon' | 'exec_overrun'
  //                   → single-reason drill-down
  const [hotFilter, setHotFilter] = React.useState(null);
  const hotOnly = hotFilter !== null;
  // v260.98 — Dynamically size the weekly cell so the table always
  // fills the available horizontal width, while staying ≥ 18px so
  // it remains readable on narrow viewports. We measure the wrapper
  // via `ResizeObserver` and recompute on every container resize.
  const wrapperRef = React.useRef(null);
  const [cellWidth, setCellWidth] = React.useState(18);
  React.useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !weekLabels.length) return undefined;
    const compute = () => {
      const available = el.clientWidth - 280;  // first sticky col = 280px
      const next = Math.max(18, Math.floor(available / weekLabels.length));
      setCellWidth(next);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [weekLabels.length]);

  // v260.136 — Public-Cloud release columns for the timeline markers.
  const releaseCols = React.useMemo(
    () => buildReleaseCols(weekLabels, data?.releases),
    [weekLabels, data],
  );
  const releaseByIdx = React.useMemo(() => releaseColsByIdx(releaseCols), [releaseCols]);

  if (!data) return null;
  if ((data.projects || []).length === 0) {
    return <EmptyState />;
  }

  // v260.98 — Compute the set of "hot" project ids once per render
  // so the row filter and the count badge use the same source of
  // truth. Criteria:
  //   1. Go-Live in next 14 days
  //   2. Currently in UAT (today inside a uat band)
  //   3. Currently in Hypercare (today inside a hypercare band)
  //   5. Execution overrun — today > execution band end_date
  //      AND status not yet approved/won (i.e. project should
  //      have moved past Execution but hasn't been signed off).
  //   6. (v260.111) Low-maturity at-risk — customer assessed as
  //      Emerging AND today falls inside Execution or UAT. Governance
  //      gaps cluster here and surfacing them on the hot panel is the
  //      cheapest way to prevent a "no one told us" sign-off failure.
  const todayIso = (data.today || '').slice(0, 10);
  const inXDays = (d, n) => {
    if (!d || !todayIso) return false;
    const dt = new Date(d + 'T00:00:00Z').getTime();
    const t = new Date(todayIso + 'T00:00:00Z').getTime();
    const days = (dt - t) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= n;
  };
  const APPROVED_STATUSES = new Set(['approved', 'won']);
  // v260.99 — Return the SET of reasons a project is hot, not just a
  // bool, so the count badge can break the total down by category on
  // hover ("12 in UAT · 6 in Hypercare · 5 going live · 3 overrun").
  // A single project can appear in multiple buckets (e.g. UAT AND
  // overrun) — the breakdown sum is therefore ≥ the de-duplicated
  // total. The toggle still operates on the unique set.
  const hotReasons = (p) => {
    const reasons = [];
    if (inXDays(p.go_live_date, 14)) reasons.push('go_live_soon');
    const bands = p.phase_bands || [];
    const todayInBand = (key) => bands.some(b =>
      b.phase_key === key && b.start_date <= todayIso && todayIso <= b.end_date
    );
    if (todayInBand('uat')) reasons.push('uat');
    if (todayInBand('hypercare')) reasons.push('hypercare');
    const exec = bands.find(b => b.phase_key === 'execution');
    if (exec && todayIso > exec.end_date && !APPROVED_STATUSES.has(p.status)) {
      reasons.push('exec_overrun');
    }
    // v260.111 — Low-maturity at-risk: Emerging customer currently in
    // a delivery-critical phase (Execution or UAT).
    if (p.maturity?.maturity_band === 'Emerging' &&
        (todayInBand('execution') || todayInBand('uat'))) {
      reasons.push('low_maturity');
    }
    return reasons;
  };
  const hotIds = new Set();
  // v260.100 — Track which projects matched each specific reason so a
  // sub-filter ("UAT", "Execution overrun", etc.) can drill the table
  // down to just that bucket on a single click.
  const idsByReason = { uat: new Set(), hypercare: new Set(), go_live_soon: new Set(), exec_overrun: new Set(), low_maturity: new Set() };
  const reasonCounts = { uat: 0, hypercare: 0, go_live_soon: 0, exec_overrun: 0, low_maturity: 0 };
  (data.projects || []).forEach(p => {
    const r = hotReasons(p);
    if (r.length) {
      hotIds.add(p.id);
      r.forEach(k => {
        reasonCounts[k] = (reasonCounts[k] || 0) + 1;
        idsByReason[k].add(p.id);
      });
    }
  });
  // Resolve the active filter to a concrete id-set used by the table.
  let visibleProjects = data.projects;
  if (hotFilter === 'all') {
    visibleProjects = data.projects.filter(p => hotIds.has(p.id));
  } else if (hotFilter && idsByReason[hotFilter]) {
    visibleProjects = data.projects.filter(p => idsByReason[hotFilter].has(p.id));
  }
  // v260.138 — Portfolio release-risk filter (from the summary tile).
  if (riskOnly) {
    visibleProjects = visibleProjects.filter(p => atRiskPhasesForProject(p).length > 0);
  }
  const REASON_LABEL = {
    uat: 'in UAT',
    hypercare: 'in Hypercare',
    go_live_soon: 'going live ≤14d',
    exec_overrun: 'Execution overrun',
    low_maturity: 'low-maturity at-risk',
  };
  const activeReasonLabel = hotFilter && hotFilter !== 'all' ? REASON_LABEL[hotFilter] : null;

  // v260.96 — Surface which phase keys actually appear in the current
  // portfolio so the legend doesn't mislead with phases nothing in the
  // window is using. Hypercare always shows the hatched swatch.
  const phaseKeysInUse = new Set();
  visibleProjects.forEach(p => {
    (p.phase_bands || []).forEach(b => {
      if (PHASE_LEGEND_LABEL[b.phase_key]) phaseKeysInUse.add(b.phase_key);
    });
  });
  const legendEntries = PHASE_ORDER.filter(k => phaseKeysInUse.has(k));
  return (
    <Card className="mt-2"><CardContent className="p-0 overflow-x-auto" data-testid="overview-project-view">
      <div className="px-3 pt-2 pb-1 flex items-center justify-between gap-3 flex-wrap" data-testid="overview-project-toolbar">
        {legendEntries.length > 0 || releaseCols.length > 0 ? (
          <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap" data-testid="overview-project-legend">
            <span>Phases:</span>
            {legendEntries.map(k => (
              <span key={k} className="inline-flex items-center gap-1">
                <span
                  className="w-3 h-2 inline-block rounded-sm"
                  style={k === 'hypercare'
                    ? { backgroundImage: `repeating-linear-gradient(45deg, ${PHASE_COLOR.hypercare}cc 0 3px, transparent 3px 6px)`, backgroundColor: `${PHASE_COLOR.hypercare}33` }
                    : { background: PHASE_COLOR[k] }}
                />
                <span>{PHASE_LEGEND_LABEL[k]}</span>
              </span>
            ))}
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1"><span className="w-0.5 h-3 bg-emerald-500 inline-block" /><span>Go-Live tick</span></span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-2 inline-block rounded-sm bg-slate-200 ring-2 ring-amber-400 ring-inset" />
              <span>Today's phase</span>
            </span>
            {releaseCols.length > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-slate-400">Releases:</span>
                {['prod', 'test', 'maintenance'].map(rt => (
                  <span key={rt} className="inline-flex items-center gap-1" data-testid={`overview-release-legend-${rt}`}>
                    <span className="w-2 h-2 rounded-full inline-block" style={{ background: RELEASE_COLORS[rt] }} />
                    <span>{RELEASE_TYPE_LABEL_SHORT[rt]}</span>
                  </span>
                ))}
              </>
            )}
          </div>
        ) : <span />}
        <div className="relative group" data-testid="overview-hot-rows-chip">
          <button
            type="button"
            onClick={() => setHotFilter(hotOnly ? null : 'all')}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors flex items-center gap-1.5 ${
              hotOnly
                ? 'bg-rose-50 border-rose-300 text-rose-700'
                : 'bg-white border-slate-200 text-slate-600 hover:border-rose-200 hover:text-rose-600'
            }`}
            title={activeReasonLabel ? `Filtered: ${activeReasonLabel} — click to clear` : 'Toggle hot rows filter'}
            data-testid="overview-hot-rows-toggle"
          >
            <Flame className="w-3 h-3" />
            <span>
              {hotFilter === 'all' && 'Showing hot rows'}
              {hotFilter === null && 'Hot rows only'}
              {activeReasonLabel && `Showing: ${activeReasonLabel}`}
            </span>
            <span className={`ml-1 px-1.5 rounded-full text-[10px] tabular-nums ${hotOnly ? 'bg-rose-200 text-rose-900' : 'bg-slate-100 text-slate-500'}`}>
              {hotFilter && hotFilter !== 'all' ? reasonCounts[hotFilter] : hotIds.size}
            </span>
          </button>
          {hotIds.size > 0 && (
            <div
              className="absolute right-0 top-full mt-1.5 z-30 hidden group-hover:block whitespace-nowrap rounded-md border border-slate-200 bg-white shadow-lg px-3 py-2 text-[11px]"
              data-testid="overview-hot-rows-breakdown"
            >
              <div className="text-slate-500 mb-1.5 text-[10px] uppercase tracking-wide flex items-center justify-between gap-3">
                <span>Hot rows breakdown — click to drill down</span>
                {hotFilter && (
                  <button
                    type="button"
                    onClick={() => setHotFilter(null)}
                    className="text-[10px] text-slate-400 hover:text-rose-600 underline"
                    data-testid="overview-hot-rows-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
              {/* v260.100 — Each chip is a one-click sub-filter that
                  drills the table down to ONLY projects matching that
                  reason. Clicking the active filter clears it. */}
              <div className="flex items-center gap-1.5">
                {[
                  { k: 'uat', label: 'in UAT', swatch: <span className="w-2 h-2 rounded-sm" style={{ background: PHASE_COLOR.uat }} /> },
                  { k: 'hypercare', label: 'in Hypercare', swatch: <span className="w-2 h-2 rounded-sm" style={{ backgroundImage: `repeating-linear-gradient(45deg, ${PHASE_COLOR.hypercare}cc 0 2px, ${PHASE_COLOR.hypercare}33 2px 4px)` }} /> },
                  { k: 'go_live_soon', label: 'going live ≤14d', swatch: <span className="w-0.5 h-3 bg-emerald-500" /> },
                  { k: 'exec_overrun', label: 'Execution overrun', swatch: <span className="w-2 h-2 rounded-sm bg-rose-500" /> },
                  { k: 'low_maturity', label: 'low-maturity at-risk', swatch: <Gauge className="w-2.5 h-2.5 text-rose-600" /> },
                ].map(({ k, label, swatch }) => {
                  const active = hotFilter === k;
                  const count = reasonCounts[k] || 0;
                  const disabled = count === 0;
                  return (
                    <button
                      key={k}
                      type="button"
                      disabled={disabled}
                      onClick={() => setHotFilter(active ? null : k)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border transition-colors ${
                        disabled
                          ? 'border-transparent text-slate-300 cursor-not-allowed'
                          : active
                            ? 'bg-rose-50 border-rose-300 text-rose-700'
                            : 'border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
                      }`}
                      data-testid={`hot-count-${k.replace('_', '-')}`}
                      title={disabled ? `No projects ${label}` : (active ? `Click to clear filter` : `Drill down to ${count} ${label}`)}
                    >
                      {swatch}
                      <span className="tabular-nums font-medium">{count}</span>
                      <span className={disabled ? '' : 'text-slate-500'}>{label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 text-[10px] text-slate-400">A project can appear in more than one bucket — total ({hotIds.size}) is de-duplicated.</div>
            </div>
          )}
        </div>
      </div>
      <div ref={wrapperRef}>
      <table
        className="text-xs"
        style={{
          // v260.98 — Cell width is now measured dynamically from the
          // container (≥18px floor, expands to fill on wide viewports)
          // so the table always stretches edge-to-edge without leaving
          // a visual gap on the right of the page.
          tableLayout: 'fixed',
          width: `${280 + cellWidth * weekLabels.length}px`,
        }}
      >
        <WeekHeader weekLabels={weekLabels} todayIdx={todayIdx} firstColLabel="Project" firstColWidth="280px" cellWidth={cellWidth} releaseCols={releaseCols} />
        <tbody>
          {visibleProjects.length === 0 ? (
            <tr><td colSpan={weekLabels.length + 1} className="text-center text-[11px] text-slate-400 py-6" data-testid="overview-hot-rows-empty">
              No projects match the hot-rows filter right now. Toggle off to see everything.
            </td></tr>
          ) : visibleProjects.map((p) => (
            <ProjectRow
              key={p.id}
              p={p}
              weekLabels={weekLabels}
              todayIdx={todayIdx}
              onOpen={onOpen}
              cellWidth={cellWidth}
              isHot={hotIds.has(p.id)}
              navigate={navigate}
              releaseByIdx={releaseByIdx}
              searchQuery={searchQuery}
            />
          ))}
        </tbody>
      </table>
      </div>
    </CardContent></Card>
  );
}

function MaturityChip({ maturity, navigate, orgId }) {
  // Pure-visual chip rendered alongside the project status badge.
  // Click jumps to the org's maturity dashboard (admins only — the
  // route is admin-gated by App.js, the chip still renders for
  // non-admins as a non-clickable tooltip target).
  if (!maturity) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[9px] text-slate-400 border border-dashed border-slate-200 rounded px-1 py-0 cursor-default"
        title="No maturity assessment on file for this customer"
        data-testid="overview-project-maturity-missing"
      >
        <Gauge className="w-2.5 h-2.5" />
        ?
      </span>
    );
  }
  const band = getMaturityBand(maturity.overall_score);
  const constraint = maturity.primary_constraint;
  const tooltip = constraint
    ? `Maturity ${Math.round(maturity.overall_score)}/100 · ${band.label}\nPrimary constraint: ${constraint.name} (${Math.round(constraint.score)}/100)`
    : `Maturity ${Math.round(maturity.overall_score)}/100 · ${band.label}`;
  const handleClick = (e) => {
    e.stopPropagation();
    if (!orgId) return;
    navigate(`/maturity?organization_id=${encodeURIComponent(orgId)}`);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      title={tooltip}
      className="inline-flex items-center gap-0.5 text-[9px] font-semibold rounded px-1.5 py-0 border hover:brightness-110 transition"
      style={{ background: band.color + '22', color: band.color, borderColor: band.color + '55' }}
      data-testid={`overview-project-maturity-${band.label.toLowerCase()}`}
    >
      <Gauge className="w-2.5 h-2.5" />
      {Math.round(maturity.overall_score)}
    </button>
  );
}

function ProjectRow({ p, weekLabels, todayIdx, onOpen, cellWidth = 18, isHot = false, navigate, releaseByIdx = {}, searchQuery = '' }) {
  // v260.97 — Proper window clamping. `findIndex(w => w >= date)`
  // returns -1 in TWO cases that need different handling:
  //   (a) date BEFORE the first week label — clamp to col 0
  //   (b) date AFTER  the last  week label — clamp to col (len-1)
  // Previously we treated all -1 cases as case (a), which made
  // phases entirely past the window end (e.g. UAT starting in Dec
  // when the window ends Oct) collapse to col 0 and repaint over
  // the entire bar with the last-rendered phase colour.
  const wlFirst = weekLabels[0];
  const wlLast = weekLabels[weekLabels.length - 1];
  const clampLeft = (d) => {
    const i = weekLabels.findIndex(w => w >= d);
    if (i >= 0) return i;
    return d < wlFirst ? 0 : weekLabels.length - 1;
  };
  const clampRight = (d) => {
    const i = weekLabels.findIndex(w => w >= d);
    if (i >= 0) return i;
    return d < wlFirst ? 0 : weekLabels.length - 1;
  };
  const left = clampLeft(p.start_date);
  const right = clampRight(p.end_date);
  const goLiveIdx = weekLabels.findIndex(w => w >= p.go_live_date);
  // v260.96 — Convert each phase_band's (start_week, end_week) into
  // calendar-week column indices inside the same `weekLabels` axis
  // used by the bar so the colored segments line up perfectly with
  // the row's start/end. Bands entirely outside the visible window
  // are dropped (return null then filter) so they don't pollute
  // the overlay stack.
  const bands = (p.phase_bands || []).map(b => {
    // Drop bands that fall entirely outside the visible window.
    if (b.end_date < wlFirst || b.start_date > wlLast) return null;
    return {
      ...b,
      leftIdx: clampLeft(b.start_date),
      rightIdx: Math.max(clampLeft(b.start_date), clampRight(b.end_date)),
    };
  }).filter(Boolean);
  const hasBands = bands.length > 0;
  const barWidthPx = (right - left + 1) * cellWidth - 2;
  // v260.137 — Deliverables that slip past their anchored release date.
  const atRiskPhases = atRiskPhasesForProject(p);
  return (
    <tr className={`border-t border-slate-100 hover:bg-slate-50 ${isHot ? 'bg-rose-50/30' : ''}`}>
      <td className={`px-3 py-2 sticky left-0 border-r border-slate-100 ${isHot ? 'bg-rose-50/60' : 'bg-white'}`} data-testid={`overview-project-row-${p.id}${isHot ? '-hot' : ''}`}>
        <button onClick={() => onOpen(p.id)} className="text-left w-full group">
          <div className="font-medium text-[#0F2040] text-xs flex items-center gap-1 group-hover:text-[#4ABFCF]">
            <Building2 className="w-3 h-3 shrink-0 text-slate-400" />
            <span className="truncate"><HighlightMatch text={p.name} query={searchQuery} /></span>
            {p.locked && (
              <span
                className="inline-flex items-center gap-0.5 shrink-0 text-[9px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1 py-px"
                title={`Effort & cost locked${p.locked_by ? ` by ${p.locked_by}` : ''}${p.locked_at ? ` on ${new Date(p.locked_at).toLocaleDateString()}` : ''}`}
                data-testid={`overview-project-locked-badge-${p.id}`}
              >
                <Lock className="w-2.5 h-2.5" /> Locked
              </span>
            )}
            {isHot && <Flame className="w-3 h-3 shrink-0 text-rose-500" title="Hot row — needs attention this week" data-testid={`overview-project-hot-flame-${p.id}`} />}
            {atRiskPhases.length > 0 && (
              <span
                className="inline-flex items-center gap-0.5 shrink-0 text-[9px] font-semibold text-rose-700 bg-rose-100 border border-rose-200 rounded px-1 py-px"
                title={`Release risk — ${atRiskPhases.length} deliverable${atRiskPhases.length === 1 ? '' : 's'} finish after the anchored release:\n${atRiskPhases.map(ph => `• ${ph.label}: slips ${ph.release_slip_days}d past ${ph.anchor_release_label}`).join('\n')}`}
                data-testid={`overview-project-risk-badge-${p.id}`}
              >
                <AlertTriangle className="w-2.5 h-2.5" />
                {atRiskPhases.length}
              </span>
            )}
            <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Badge variant="secondary" className={`text-[9px] px-1.5 py-0 ${STATUS_TONE[p.status] || 'bg-slate-100 text-slate-600'}`}>{p.status}</Badge>
            {p.segment && (
              <span className={`text-[9px] font-semibold rounded-full px-1.5 py-0.5 border shrink-0 ${SEGMENT_TONE[p.segment] || 'bg-slate-50 text-slate-600 border-slate-200'}`}
                data-testid={`overview-project-segment-${p.id}`}>
                {SEGMENT_LABEL[p.segment] || p.segment}
              </span>
            )}
            <MaturityChip maturity={p.maturity} navigate={navigate} orgId={p.org_id} />
            <span className="text-[10px] text-slate-400">{fmtDate(p.start_date)} → {fmtDate(p.go_live_date)} · {p.duration_weeks}w</span>
          </div>
        </button>
      </td>
      {weekLabels.map((wkLabel, i) => (
        <td key={wkLabel || i} className={`relative ${i === todayIdx ? 'bg-amber-50/60' : ''}`} style={{ width: cellWidth }}>
          {/* v260.136 — Public-Cloud release marker: full-height tinted
              rule so the release line reads down through the deliverable
              bars, just like the Planner timeline. */}
          {releaseByIdx[i] && releaseByIdx[i].map((r, k) => (
            <div
              key={r.id || `rel-${i}-${k}`}
              className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px z-20 pointer-events-none"
              style={{ background: (RELEASE_COLORS[r.release_type] || '#64748B'), opacity: 0.45 }}
              title={`${r.label || 'Release'}${r.date ? ` · ${r.date}` : ''}`}
            />
          ))}
          {i === left && (
            hasBands ? (
              // Phase-banded bar — each band rendered as an absolute
              // colored segment inside a single transparent track so
              // overlapping phases stack visually. Hypercare gets a
              // hatched fill so the post-go-live tail reads as a
              // ramp-down even at 18px-per-week density.
              <div
                className="absolute top-1 h-4 rounded-sm overflow-hidden"
                style={{ left: 1, width: `${barWidthPx}px`, background: '#E5E7EB' }}
                title={`${fmtDate(p.start_date)} → ${fmtDate(p.end_date)}`}
                data-testid={`overview-project-bar-${p.id}`}
              >
                {bands.map((b, bi) => {
                  const segLeft = (b.leftIdx - left) * cellWidth;
                  const segWidth = (b.rightIdx - b.leftIdx + 1) * cellWidth;
                  const color = PHASE_COLOR[b.phase_key] || PHASE_COLOR.other;
                  const isHyper = b.phase_key === 'hypercare';
                  // v260.97 — Surface which phase each project is
                  // currently in: if `today` falls inside this band's
                  // calendar range, draw a soft pulse ring so leaders
                  // can scan the portfolio and instantly see whose
                  // execution / UAT / hypercare is live right now.
                  const isCurrent =
                    todayIdx >= 0 &&
                    todayIdx >= b.leftIdx &&
                    todayIdx <= b.rightIdx;
                  return (
                    <div
                      key={`${b.phase_key}-${bi}-${b.start_week}`}
                      className={`absolute top-0 h-full ${isCurrent ? 'ring-2 ring-amber-400 ring-inset z-10' : ''}`}
                      style={{
                        left: `${segLeft}px`,
                        width: `${segWidth}px`,
                        background: isHyper
                          ? `repeating-linear-gradient(45deg, ${color}cc 0 3px, ${color}33 3px 6px)`
                          : color,
                        opacity: isHyper ? 0.85 : 0.9,
                      }}
                      title={`${b.phase}${isCurrent ? ' (current)' : ''} · ${fmtDate(b.start_date)} → ${fmtDate(b.end_date)}`}
                      data-testid={`overview-project-band-${p.id}-${b.phase_key}${isCurrent ? '-current' : ''}`}
                    />
                  );
                })}
              </div>
            ) : (
              <div
                className="absolute top-1 h-4 bg-[#0F2040]/80 rounded-sm"
                style={{ left: 1, width: `${barWidthPx}px` }}
                title={`${fmtDate(p.start_date)} → ${fmtDate(p.end_date)}`}
                data-testid={`overview-project-bar-${p.id}`}
              />
            )
          )}
          {i === goLiveIdx && goLiveIdx >= 0 && (
            <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-emerald-500" title={`Go-Live ${fmtDate(p.go_live_date)}`} />
          )}
        </td>
      ))}
    </tr>
  );
}

// ─── Resource View ────────────────────────────────────────────────────

function ResourceView({ data, weekLabels, todayIdx, granularity, onOpen, searchQuery = '' }) {
  const [pulseWeekIdx, setPulseWeekIdx] = useState(-1);
  const pulseTimerRef = React.useRef(null);

  React.useEffect(() => () => {
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
  }, []);

  const focusWeek = useCallback((idx) => {
    if (idx < 0) return;
    setPulseWeekIdx(idx);
    // Scroll the column into view inside the overflow-x-auto card
    try {
      const cell = document.querySelector(
        `[data-testid="overview-resource-view"] th[data-week-idx="${idx}"]`
      );
      if (cell) cell.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    } catch (scrollErr) {
      // Scroll-into-view can throw in jsdom / older browsers — non-fatal,
      // the pulse highlight still applies. Log so debugging is possible.
      logger.warn('focusWeek scrollIntoView failed', scrollErr);
    }
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => setPulseWeekIdx(-1), 1800);
  }, []);

  if (!data) return null;
  const totals = granularity === 'discipline'
    ? (data.totals?.per_discipline || []).map(d => ({ key: d.discipline, label: d.discipline, weekly_fte_pct: d.weekly_fte_pct, weekly_hours: d.weekly_hours, color: DISCIPLINE_COLOR[d.discipline] }))
    : (data.totals?.per_role || []).map(r => ({ key: r.role, label: r.role_label, weekly_fte_pct: r.weekly_fte_pct, weekly_hours: r.weekly_hours, color: DISCIPLINE_COLOR[r.discipline] }));

  if (totals.length === 0) return <EmptyState />;
  return (
    <div data-testid="overview-export-region">
      <PeakFteCallouts totals={totals} weekLabels={weekLabels} granularity={granularity} onFocusWeek={focusWeek} />
      <Card className="mt-2"><CardContent className="p-0 overflow-x-auto" data-testid="overview-resource-view">
      <div className="px-3 pt-2 pb-1 text-[10px] text-slate-500 flex items-center gap-2 flex-wrap" data-testid="overview-resource-legend">
        <span>Cell value = <strong className="text-[#0F2040]">FTE</strong> (1.0 = one full-time person · {data.fte_hours_per_week || 40}h/wk)</span>
        <span className="text-slate-300">·</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-cyan-100 inline-block" />light</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-amber-200 inline-block" />near full</span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-flex"><span className="w-2 h-2 bg-rose-300 inline-block" /><span className="w-2 h-2 bg-rose-400 inline-block" /><span className="w-2 h-2 bg-rose-500 inline-block" /><span className="w-2 h-2 bg-rose-600 inline-block" /><span className="w-2 h-2 bg-rose-700 inline-block" /></span>
          <span>1 → 7+ FTE (deeper = hotter)</span>
        </span>
      </div>
      <table className="w-full text-xs">
        <WeekHeader weekLabels={weekLabels} todayIdx={todayIdx} firstColLabel={granularity === 'discipline' ? 'Discipline' : 'Role'} firstColWidth="220px" pulseWeekIdx={pulseWeekIdx} />
        <tbody>
          {totals.map((row) => (
            <tr key={row.key} className="border-t border-slate-100">
              <td className="px-3 py-1.5 sticky left-0 bg-white border-r border-slate-100">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: row.color || '#94A3B8' }} />
                  <span className="font-medium text-[#0F2040] text-xs">{row.label}</span>
                </div>
              </td>
              {row.weekly_fte_pct.map((pct, i) => {
                const hrs = row.weekly_hours[i] || 0;
                const fte = (pct || 0) / 100;
                const isPulse = i === pulseWeekIdx;
                const wkLabel = weekLabels[i];
                return (
                  <td key={wkLabel || i} className={`text-center ${i === todayIdx ? 'ring-1 ring-amber-300 ring-inset' : ''} ${isPulse ? 'ring-2 ring-amber-400 ring-inset animate-pulse' : ''}`} style={{ width: 18 }}
                    title={`${row.label} · ${fmtDate(wkLabel)}: ${hrs}h (${fte.toFixed(1)} FTE)`}>
                    <div className={`${fteTone(fte)} h-5 flex items-center justify-center text-[8px] tabular-nums ${fteTextTone(fte)}`}>
                      {fmtFte(pct)}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tbody data-export-skip="true">
          <tr className="border-t-2 border-slate-200 bg-slate-50">
            <td className="px-3 py-1.5 sticky left-0 bg-slate-50 border-r border-slate-100 text-[10px] uppercase tracking-wide text-slate-500">Per-project breakdown</td>
            <td colSpan={weekLabels.length} className="text-[10px] text-slate-400 px-3 py-1.5">Click any project below to drill into its Planner</td>
          </tr>
          {(data.projects || []).map((p) => (
            <ProjectResourceRow key={p.id} p={p} weekLabels={weekLabels} todayIdx={todayIdx} granularity={granularity} onOpen={onOpen} searchQuery={searchQuery} />
          ))}
        </tbody>
      </table>
    </CardContent></Card>
    </div>
  );
}

// Peak-FTE callout strip: surfaces the top capacity hot-spots so leadership
// doesn't have to scan the heatmap manually. Per discipline/role we find the
// week with the highest aggregate FTE; we sort descending and show the top 4
// cards. Cards with peak ≥ 1.0 FTE are emphasised (rose) — that's the
// "need at least one person dedicated" threshold; lighter cards (peak < 1)
// stay neutral so the strip stays calm on small workspaces.
function PeakFteCallouts({ totals, weekLabels, granularity, onFocusWeek }) {
  const peaks = useMemo(() => {
    const out = [];
    for (const row of totals || []) {
      const arr = row.weekly_fte_pct || [];
      let bestIdx = -1;
      let bestPct = -1;
      for (let i = 0; i < arr.length; i++) {
        if ((arr[i] || 0) > bestPct) {
          bestPct = arr[i] || 0;
          bestIdx = i;
        }
      }
      if (bestIdx >= 0 && bestPct > 0) {
        out.push({
          key: row.key,
          label: row.label,
          color: row.color,
          fte: bestPct / 100,
          weekIdx: bestIdx,
          week: weekLabels[bestIdx] || '',
          hrs: (row.weekly_hours || [])[bestIdx] || 0,
          series: arr,
          peakPct: bestPct,
        });
      }
    }
    out.sort((a, b) => b.fte - a.fte);
    return out.slice(0, 4);
  }, [totals, weekLabels]);

  if (peaks.length === 0) return null;
  return (
    <div className="mt-2 mb-2" data-testid="overview-peak-callouts">
      <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-wide text-slate-500">
        <Flame className="w-3 h-3 text-rose-500" />
        <span>Peak capacity {granularity === 'role' ? 'by role' : 'by discipline'}</span>
        <span className="text-slate-300">·</span>
        <span className="italic normal-case tracking-normal">click a card to jump to that week</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {peaks.map((p) => {
          const hot = p.fte >= 1;
          const handle = () => onFocusWeek && onFocusWeek(p.weekIdx);
          return (
            <button
              type="button"
              key={p.key}
              onClick={handle}
              data-testid={`overview-peak-${p.key}`}
              className={`text-left rounded-md border px-2.5 py-2 transition hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${
                hot ? 'border-rose-200 bg-rose-50 hover:bg-rose-100' : 'border-slate-200 bg-white hover:bg-slate-50'
              }`}
              title={`${p.label}: peaks at ${p.fte.toFixed(1)} FTE (${p.hrs}h) the week of ${fmtDate(p.week)} — click to jump`}
            >
              <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color || '#94A3B8' }} />
                <span className="truncate font-medium text-[#0F2040]">{p.label}</span>
              </div>
              <div className="flex items-baseline gap-1 mt-0.5">
                <span className={`text-lg font-bold tabular-nums ${hot ? 'text-rose-700' : 'text-[#0F2040]'}`}>
                  {p.fte.toFixed(1)}
                </span>
                <span className={`text-[10px] ${hot ? 'text-rose-600' : 'text-slate-500'}`}>FTE</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                wk of <span className="text-[#0F2040]">{fmtDate(p.week)}</span>
              </div>
              <Sparkline
                values={p.series}
                peakIdx={p.weekIdx}
                weekLabels={weekLabels}
                hot={hot}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Compact 27-week bar sparkline. Renders the row's weekly FTE % series as
// thin vertical bars normalised to the row's own peak so a "one-off spike
// vs sustained load" contrast is immediately readable. The peak bar is
// rendered in the card's accent colour (rose when hot, slate-700
// otherwise); other bars use a muted hue.
function Sparkline({ values, peakIdx, weekLabels, hot }) {
  if (!values || values.length === 0) return null;
  const max = Math.max(1, ...values.map(v => v || 0));
  const width = 144;
  const height = 22;
  const gap = 1;
  const barW = Math.max(1, (width - (values.length - 1) * gap) / values.length);
  const peakColor = hot ? '#BE123C' : '#0F2040';
  const baseColor = hot ? '#FECDD3' : '#CBD5E1';
  return (
    <svg
      role="img"
      aria-label="27-week trend"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="mt-1.5 block"
      data-testid="peak-sparkline"
    >
      {values.map((v, i) => {
        const h = Math.max(1, ((v || 0) / max) * (height - 2));
        const x = i * (barW + gap);
        const y = height - h;
        const isPeak = i === peakIdx;
        const wkLabel = weekLabels[i] || '';
        return (
          <rect
            key={wkLabel || i}
            x={x}
            y={y}
            width={barW}
            height={h}
            fill={isPeak ? peakColor : baseColor}
            opacity={isPeak ? 1 : 0.8}
          >
            <title>{`${fmtDate(wkLabel)}: ${((v || 0) / 100).toFixed(1)} FTE`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

function ProjectResourceRow({ p, weekLabels, todayIdx, granularity, onOpen, searchQuery = '' }) {
  const rolled = useMemo(() => {
    if (granularity === 'role') return p.role_allocation;
    const byDisc = {};
    for (const r of p.role_allocation || []) {
      const d = byDisc[r.discipline] || { discipline: r.discipline, weekly_fte_pct: [], weekly_hours: [] };
      r.weekly_fte_pct.forEach((v, i) => d.weekly_fte_pct[i] = Math.round(((d.weekly_fte_pct[i] || 0) + v) * 10) / 10);
      r.weekly_hours.forEach((v, i) => d.weekly_hours[i] = Math.round(((d.weekly_hours[i] || 0) + v) * 10) / 10);
      byDisc[r.discipline] = d;
    }
    return Object.values(byDisc);
  }, [p, granularity]);
  return (
    <>
      <tr className="border-t border-slate-100 bg-slate-50/30">
        <td className="px-3 py-1.5 sticky left-0 bg-slate-50/30 border-r border-slate-100">
          <button onClick={() => onOpen(p.id)} className="text-left w-full group">
            <span className="font-medium text-[10px] text-[#0F2040] flex items-center gap-1 group-hover:text-[#4ABFCF]">
              <Building2 className="w-2.5 h-2.5 shrink-0 text-slate-400" />
              <span className="truncate"><HighlightMatch text={p.name} query={searchQuery} /></span>
              {p.locked && <Lock className="w-2.5 h-2.5 shrink-0 text-amber-700" title="Effort & cost locked" data-testid={`overview-project-locked-icon-${p.id}`} />}
            </span>
          </button>
        </td>
        <td colSpan={weekLabels.length} className="text-[10px] text-slate-400 px-3 py-1 italic">
          {fmtDate(p.start_date)} → {fmtDate(p.end_date)} · {p.total_hours}h total
        </td>
      </tr>
      {rolled.map((r, idx) => (
        <tr key={`${p.id}-${r.role || r.discipline || idx}`} className="border-t border-slate-50">
          <td className="px-3 py-1 sticky left-0 bg-white border-r border-slate-100">
            <span className="text-[10px] text-slate-500 pl-3 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: DISCIPLINE_COLOR[r.discipline] || '#94A3B8' }} />
              {r.role_label || r.discipline}
            </span>
          </td>
          {(r.weekly_fte_pct || []).map((pct, i) => {
            const hrs = (r.weekly_hours || [])[i] || 0;
            const fte = (pct || 0) / 100;
            const wkLabel = weekLabels[i];
            return (
              <td key={wkLabel || i} className={`text-center ${i === todayIdx ? 'ring-1 ring-amber-300 ring-inset' : ''}`} style={{ width: 18 }}
                title={`${r.role_label || r.discipline}: ${hrs}h (${fte.toFixed(1)} FTE) wk of ${fmtDate(wkLabel)}`}>
                <div className={`${fteTone(fte)} h-4 flex items-center justify-center text-[8px] tabular-nums ${fteTextTone(fte)}`}>
                  {fmtFte(pct)}
                </div>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

// ─── Development View ─────────────────────────────────────────────────

